import { z } from 'zod';

export const ticketStatusSchema = z.enum([
  'OPEN',
  'IN_PROGRESS',
  'WAITING_CLIENT',
  'RESOLVED',
  'CLOSED',
]);
export type TicketStatus = z.infer<typeof ticketStatusSchema>;

export const ticketSourceSchema = z.enum(['PORTAL', 'EMAIL']);
export type TicketSource = z.infer<typeof ticketSourceSchema>;

export const ticketPrioritySchema = z.enum(['LOW', 'NORMAL', 'HIGH', 'URGENT']);
export type TicketPriority = z.infer<typeof ticketPrioritySchema>;

export const ticketCategorySchema = z.enum([
  'CONTENT_UPDATE',
  'BUG',
  'EMERGENCY',
  'QUESTION',
  'MAINTENANCE',
]);
export type TicketCategory = z.infer<typeof ticketCategorySchema>;

export const ticketCreateSchema = z
  .object({
    title: z
      .string()
      .trim()
      .min(3, 'Title must be at least 3 characters')
      .max(200, 'Title must be 200 characters or fewer'),
    description: z
      .string()
      .min(1, 'Description is required')
      .max(20000, 'Description must be 20000 characters or fewer'),
    companyId: z.string().min(1).optional(),
    source: ticketSourceSchema.default('PORTAL'),
    priority: ticketPrioritySchema.default('NORMAL'),
    category: ticketCategorySchema.default('CONTENT_UPDATE'),
    // Assignment + website link are set by ADMIN/SUPPORT; the service ignores
    // these from CLIENT callers. Optional on create.
    assignedToId: z.string().min(1).optional(),
    websiteId: z.string().min(1).optional(),
    isBillable: z.boolean().default(true),
    usesIncrements: z.boolean().default(true),
  })
  .strict();
export type TicketCreateInput = z.infer<typeof ticketCreateSchema>;

/**
 * ticketUpdateSchema — partial update. TKT-03: whenever isBillable is
 * explicitly toggled (true or false), a non-empty billableReason MUST
 * accompany the change. Enforced via superRefine so the rule is part of
 * the contract layer (defense-in-depth on top of the service rule).
 */
export const ticketUpdateSchema = z
  .object({
    title: z.string().trim().min(3).max(200).optional(),
    description: z.string().min(1).max(20000).optional(),
    status: ticketStatusSchema.optional(),
    source: ticketSourceSchema.optional(),
    priority: ticketPrioritySchema.optional(),
    category: ticketCategorySchema.optional(),
    // Nullable so an assignee / website link can be cleared (unassign / unlink).
    assignedToId: z.string().min(1).nullable().optional(),
    websiteId: z.string().min(1).nullable().optional(),
    isBillable: z.boolean().optional(),
    usesIncrements: z.boolean().optional(),
    billableReason: z
      .string()
      .trim()
      .min(1)
      .max(500, 'Billable reason must be 500 characters or fewer')
      .optional(),
    resolvedAt: z.string().datetime().nullable().optional(),
  })
  .strict()
  .superRefine((val, ctx) => {
    if (val.isBillable !== undefined) {
      if (!val.billableReason || val.billableReason.trim().length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['billableReason'],
          message: 'billableReason is required when isBillable is changed (TKT-03)',
        });
      }
    }
  });
export type TicketUpdateInput = z.infer<typeof ticketUpdateSchema>;

export const ticketBillableSchema = z
  .object({
    isBillable: z.boolean(),
    billableReason: z
      .string()
      .trim()
      .min(1, 'billableReason is required')
      .max(500, 'billableReason must be 500 characters or fewer'),
    usesIncrements: z.boolean().optional(),
  })
  .strict();
export type TicketBillableInput = z.infer<typeof ticketBillableSchema>;

export const ticketSummarySchema = z.object({
  id: z.string(),
  companyId: z.string(),
  title: z.string(),
  description: z.string(),
  status: ticketStatusSchema,
  source: ticketSourceSchema,
  priority: ticketPrioritySchema,
  category: ticketCategorySchema,
  assignedToId: z.string().nullable(),
  websiteId: z.string().nullable(),
  isBillable: z.boolean(),
  billableReason: z.string().nullable(),
  usesIncrements: z.boolean(),
  createdById: z.string().nullable(),
  resolvedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type TicketSummary = z.infer<typeof ticketSummarySchema>;

export const ticketListFilterSchema = z
  .object({
    status: ticketStatusSchema.optional(),
    source: ticketSourceSchema.optional(),
    priority: ticketPrioritySchema.optional(),
    category: ticketCategorySchema.optional(),
    assignedToId: z.string().min(1).optional(),
    companyId: z.string().min(1).optional(),
    billable: z.boolean().optional(),
    cursor: z.string().min(1).optional(),
    limit: z.number().int().min(1).max(100).default(25),
  })
  .strict();
export type TicketListFilter = z.infer<typeof ticketListFilterSchema>;

/**
 * Resend inbound webhook payload (Resend docs: email.received event).
 * Top-level and `data` object are strict; `headers` is a loose Record.
 */
export const inboundEmailPayloadSchema = z
  .object({
    type: z.literal('email.received'),
    created_at: z.string().datetime(),
    data: z
      .object({
        from: z
          .object({
            email: z.string().email(),
            name: z.string().optional(),
          })
          .strict(),
        to: z
          .array(
            z
              .object({
                email: z.string().email(),
              })
              .strict(),
          )
          .min(1),
        subject: z.string().default(''),
        text: z.string().default(''),
        html: z.string().optional(),
        headers: z.record(z.string()),
      })
      .strict(),
  })
  .strict();
export type InboundEmailPayload = z.infer<typeof inboundEmailPayloadSchema>;

export const kbSuggestQuerySchema = z
  .object({
    q: z
      .string()
      .trim()
      .min(1, 'Query is required')
      .max(200, 'Query must be 200 characters or fewer'),
    limit: z.number().int().min(1).max(20).default(5),
  })
  .strict();
export type KbSuggestQuery = z.infer<typeof kbSuggestQuerySchema>;
